/**
 * Logo-generation helpers for step 3 — ported 1:1 from the Deploy module
 * (`components/modules/deploy/utils`), kept page-local like the region/currency
 * helpers (the area is hand-written; we don't import from the config-engine code).
 *
 * The flow: `/art/themes` yields a theme list + the full option set; each batch
 * requests `count` logos = `count − 1` themes taken in a wrapping window over the
 * theme list, plus the one "creative" theme (the element present in `themes` but
 * not in `options`). Each `fetchLogo` POST returns immediately; the actual image
 * arrives later over the websocket and lands in `mlServices.logosSuggestions`.
 */

import type { SiteContext } from '@/store/slices/ml-services';
import { CUST_LOGO_SEED_FALLBACK } from '../../../theme/customizer-tokens';

/** One generated logo as rendered by step 3 (mapped from `mlServices.logosSuggestions`). */
export interface LogoSuggestion {
  cdnUrl: string;
  /** Per-logo blurb from the art API (the card caption); may be empty. */
  description: string;
}

/** Colours the logo generator is seeded with, read from the configurator tokens. */
export interface LogoTokenValues {
  primaryColor: string;
  secondaryColor: string;
  background: string;
}

/** The element that appears an odd number of times across the two arrays — the
 *  "creative" theme present in `themes` but not in `options`. */
export function findExtraElement<T>(arr1: T[], arr2: T[]): T | undefined {
  const combined = [...arr1, ...arr2];
  const counts = new Map<T, number>();
  for (const el of combined) counts.set(el, (counts.get(el) ?? 0) + 1);
  for (const [el, count] of counts.entries()) {
    if (count % 2 !== 0) return el;
  }
  return undefined;
}

interface NextSliceResult<T> {
  nextSlice: T[];
  nextStartIndex: number;
}

/** Take `sliceSize` items starting at `startIndex`, wrapping around the end. */
export function getCircularSlice<T>(items: T[], sliceSize: number, startIndex: number): NextSliceResult<T> {
  if (!items || items.length === 0 || sliceSize <= 0) {
    return { nextSlice: [], nextStartIndex: startIndex };
  }

  const potentialEndIndex = startIndex + sliceSize;
  let nextSlice: T[];
  let nextStartIndex: number;

  if (potentialEndIndex > items.length) {
    const remainingItems = items.slice(startIndex);
    const needed = sliceSize - remainingItems.length;
    const wrappedItems = items.slice(0, needed);
    nextSlice = [...remainingItems, ...wrappedItems];
    nextStartIndex = needed;
  } else {
    nextSlice = items.slice(startIndex, potentialEndIndex);
    nextStartIndex = potentialEndIndex === items.length ? 0 : potentialEndIndex;
  }

  return { nextSlice, nextStartIndex };
}

/**
 * Pull the generator seed colours out of the configurator token map.
 *
 * Per-field fallback: use whatever seed colours the theme actually provides,
 * defaulting ONLY the specific missing one. Previously a single absent key (e.g.
 * `--top-nav-surface`) made this return `undefined`, so the caller's
 * `?? CUST_LOGO_SEED_FALLBACK` swapped ALL THREE colours to defaults — discarding
 * the brand's real primary/secondary. We still return `undefined` when there is no
 * token map at all, so the caller's whole-object fallback covers that case.
 */
export function getValuesFromTokens(tokens?: Record<string, string>): LogoTokenValues | undefined {
  if (!tokens) return undefined;
  const surface = tokens['--top-nav-surface'];
  return {
    primaryColor: tokens['--primary-fill-default'] || CUST_LOGO_SEED_FALLBACK.primaryColor,
    secondaryColor: tokens['--secondary-fill-default'] || CUST_LOGO_SEED_FALLBACK.secondaryColor,
    background: surface ? surfaceColor(surface) : CUST_LOGO_SEED_FALLBACK.background,
  };
}

/**
 * The colour the logo sits on, from a CSS `background` token value.
 *
 * `--top-nav-surface` may be a full gradient shorthand. The generator wants a colour:
 * it words the value into the prompt ("displayed on: ...") and caps it at 100 chars, so
 * raw CSS reads as noise and a longer gradient fails validation (no logo at all). The
 * FIRST stop is the right pick for the top nav: its gradients start at the top, which
 * is where the logo is.
 */
export function surfaceColor(background: string): string {
  const stop = background.match(/#[0-9a-f]{3,8}\b|rgba?\([^)]*\)|hsla?\([^)]*\)/i);
  return /gradient\(/i.test(background) && stop ? stop[0] : background.trim();
}

/** The catalog slices the resolver reads (a subset of `ConfiguratorConfigRoot`). */
export interface SiteContextCatalog {
  site_art_styles: { id: number; name: string }[];
  site_seed_groups: { name: string; site_skins: { id: number; name: string }[] }[];
}

/** The saved design keys the resolver reads (a subset of `configuration_data`). */
export interface SiteContextDesign {
  artStyleId?: number;
  siteSkinId?: number;
  countryCode?: string;
}

/**
 * Resolve the site's design ids to the NAMES spark-ai ranks and prompts with.
 * Ids the catalog does not know are simply left out (no key), never sent as blanks.
 */
export function resolveSiteContext(
  catalog: SiteContextCatalog | null | undefined,
  design: SiteContextDesign | undefined,
): SiteContext {
  const context: SiteContext = {};
  if (design?.countryCode) context.market = design.countryCode;
  if (!catalog || !design) return context;
  const style = catalog.site_art_styles.find((s) => s.id === design.artStyleId);
  if (style) context.art_style = style.name;
  for (const group of catalog.site_seed_groups) {
    const skin = group.site_skins.find((s) => s.id === design.siteSkinId);
    if (skin) {
      context.seed_group = group.name;
      // The catalog marks each group's base skin as "<name> [seed]"; that marker is
      // CMS bookkeeping, not part of the look, and would land verbatim in the prompt.
      context.skin = skin.name.replace(/\s*\[seed\]\s*$/i, '');
      break;
    }
  }
  return context;
}
